import cloneDeep from 'lodash.clonedeep';
import { WidgetType } from 'types/widget.types';
import { PieChartWidgetData } from 'widgets/ChartWidgets/PieChartWidget/PieChartWidget.types';

import { DEFAULT_TEXT_STYLE } from '../ChartWidget.config';
import { CustomLegendRender, applyPatterns, formatDataLabel, getPrefixSuffix } from '../ChartWidget.helpers';
import { HighchartsSeriesData } from '../ChartWidget.hooks';
import { HighchartsOptions } from '../DataTableChartCommon/DataTableChartCommon.types';
import {
  DEFAULT_DONUT_PIE_INNER_SIZE,
  DEFAULT_HEIGHT,
  DEFAULT_PIE_BORDER_COLOR,
  DEFAULT_PIE_BORDER_WIDTH,
  DEFAULT_PIE_CHART_DATA,
  DEFAULT_PIE_INNER_SIZE,
  DEFAULT_PIE_NUMBER_FORMAT,
  DEFAULT_WIDTH,
} from './PieChartWidget.config';
import { VERSION } from './PieChartWidget.upgrade';

/**
 * Generate default PieChartWidget data
 */
export const generateDefaultData = () => {
  return {
    widgetType: WidgetType.PieChart,
    widgetData: {
      version: VERSION,
      widthPx: DEFAULT_WIDTH,
      heightPx: DEFAULT_HEIGHT,
      topPx: 0,
      leftPx: 0,
      rotateDeg: 0,
      altText: '',
      isDecorative: false,
      patterns: {
        enabled: false,
        list: [],
      },
      ...DEFAULT_PIE_CHART_DATA,
    } as PieChartWidgetData,
  };
};

/**
 * Generate default DonutPieChartWidget data
 */
export const generateDonutPieDefaultData = () => {
  const defaultData = generateDefaultData();

  return {
    ...defaultData,
    widgetData: {
      ...defaultData.widgetData,
      generalOptions: {
        ...defaultData.widgetData.generalOptions,
        innerSize: DEFAULT_DONUT_PIE_INNER_SIZE,
      },
    },
  };
};

/**
 * @param defaultOptions Is modified by reference! Ensure that you're always passing a new object.
 * @param widgetData
 * @param categories
 * @param series
 * @param keyCustom
 */
export const getHighChartsPieConfig = (
  defaultOptions: HighchartsOptions,
  widgetData: PieChartWidgetData,
  categories: Array<string>,
  series: HighchartsSeriesData[],
  keyCustom: number,
) => {
  const options = defaultOptions;
  const { enabled: patternsEnabled, list: patternsList } = widgetData.patterns;

  const {
    dataLabels: { enabled: enableDataLabel, prefix, suffix, style: dataLabelStyle, categoryEnabled, valueEnabled },
    generalOptions: {
      borderWidth = DEFAULT_PIE_BORDER_WIDTH,
      borderColor = DEFAULT_PIE_BORDER_COLOR,
      innerSize = DEFAULT_PIE_INNER_SIZE,
    } = {},
    legend: { enabled: enableLegend },
    numberFormat: { format = DEFAULT_PIE_NUMBER_FORMAT, precision, separator } = {},
  } = widgetData;

  options.chart = options.chart || {};
  options.chart.type = 'pie';

  options.chart.events = { render: CustomLegendRender };

  options.keyCustom = keyCustom;
  options.xAxis = options.xAxis || {};
  (options.xAxis as Highcharts.AxisOptions).categories = Array.from(categories);

  options.series = series.map(({ name, color, data }) => ({
    name,
    color,
    data: cloneDeep(data), // Avoid modifying the original when applying patterns
    visible: true,
    state: { hover: { enabled: false } },
    type: 'pie',
  }));

  const animation = options.plotOptions?.series?.animation || false;
  options.plotOptions = {};
  options.plotOptions.series = {
    enableMouseTracking: false,
    shadow: false,
    animation,
  };

  // for old pie charts without the category and value label toggles - use the data label toggle
  const isCategoryEnabled = typeof categoryEnabled === 'undefined' ? enableDataLabel : categoryEnabled;
  const isValueEnabled = typeof valueEnabled === 'undefined' ? enableDataLabel : valueEnabled;
  const isEnableLabel = isCategoryEnabled || isValueEnabled;

  options.plotOptions.pie = {
    allowPointSelect: true,
    cursor: 'pointer',
    showInLegend: enableLegend,
    borderWidth,
    borderColor,
    innerSize: Math.min(innerSize, 90) + '%',
    dataLabels: {
      enabled: isEnableLabel,
      style: {
        ...DEFAULT_TEXT_STYLE,
        textOverflow: 'wrap',
        color: dataLabelStyle.color,
        fontSize: dataLabelStyle.fontSize,
        fontFamily: dataLabelStyle.fontFamily,
        fontWeight: dataLabelStyle.fontWeight,
        fontStyle: dataLabelStyle.fontStyle,
        textDecoration: dataLabelStyle.textDecoration,
      },
      connectorShape: 'straight',
      formatter: function () {
        if (format === 'percentage') {
          const adjustedPrecision = precision === undefined || precision === 'Auto' ? '0' : precision;

          if (isCategoryEnabled && isValueEnabled) {
            return `${this.point.name}: ${formatDataLabel({
              dataLabel: this.percentage,
              precision: adjustedPrecision,
            })}%`;
          }

          if (!isCategoryEnabled && isValueEnabled) {
            return `${formatDataLabel({ dataLabel: this.percentage, precision: adjustedPrecision })}%`;
          }

          if (isCategoryEnabled && !isValueEnabled) {
            return `${this.point.name}`;
          }

          return `${this.point.name}: ${formatDataLabel({
            dataLabel: this.percentage,
            precision: adjustedPrecision,
          })}%`;
        }

        const { prefix: customPrefix = prefix, suffix: customSuffix = suffix } = getPrefixSuffix({
          data: this.series.data[this.point.index],
          prefix,
          suffix,
        });

        if (isCategoryEnabled && isValueEnabled) {
          return `${this.point.name}: ${formatDataLabel({
            dataLabel: this.y,
            prefix: customPrefix,
            suffix: customSuffix,
            precision,
            separator,
          })}`;
        }

        if (!isCategoryEnabled && isValueEnabled) {
          return `${formatDataLabel({
            dataLabel: this.y,
            prefix: customPrefix,
            suffix: customSuffix,
            precision,
            separator,
          })}`;
        }

        if (isCategoryEnabled && !isValueEnabled) {
          return `${this.point.name}`;
        }

        return `${this.point.name}: ${formatDataLabel({
          dataLabel: this.y,
          prefix: customPrefix,
          suffix: customSuffix,
          precision,
          separator,
        })}`;
      },
    },
  };

  if (patternsEnabled) {
    applyPatterns(options, patternsList);
  }

  return options;
};
